import { addHours, isAfter, isValid } from "date-fns";
import type { FormValues } from "./AnnouncementDialog";
import {
  AnnouncementItem,
  Severity,
  TargetGroup,
  type AnnouncementPayload,
  type SeverityType,
} from "./types";

export const LOCAL_STORAGE_READ_ANNOUNCEMENTS = "userReadAnnouncements";

export const getReadAnnouncements = () => {
  const readAnnouncements = localStorage.getItem(
    LOCAL_STORAGE_READ_ANNOUNCEMENTS,
  );
  return readAnnouncements ? JSON.parse(readAnnouncements) : [];
};

export const markReadAnnouncement = (announcementId: string) => {
  const readAnnouncements = getReadAnnouncements();
  if (!readAnnouncements.includes(announcementId)) {
    readAnnouncements.push(announcementId);
    localStorage.setItem(
      LOCAL_STORAGE_READ_ANNOUNCEMENTS,
      JSON.stringify(readAnnouncements),
    );
  }
};

export const removeAnnouncementFromLocalStorage = (announcementId: string) => {
  const readAnnouncements = getReadAnnouncements();
  const updatedReadAnnouncements = readAnnouncements.filter(
    (id: string) => id !== announcementId,
  );
  localStorage.setItem(
    LOCAL_STORAGE_READ_ANNOUNCEMENTS,
    JSON.stringify(updatedReadAnnouncements),
  );
};

export const isExpertAnnouncement = (
  targetGroups: (typeof TargetGroup)[keyof typeof TargetGroup][],
) => {
  return targetGroups.includes(TargetGroup.Expert);
};

export const getAnnouncementFormValues = (
  announcement: AnnouncementItem | null,
): FormValues => ({
  dateRange: announcement
    ? [new Date(announcement.startAt), new Date(announcement.endAt)]
    : [new Date(), addHours(new Date(), 2)],
  appTypes: announcement
    ? announcement.targetGroups
    : [TargetGroup.Expert, TargetGroup.Customer, TargetGroup.Talent],
  contentFi: announcement
    ? announcement.messages.find((m) => m.languageCode === "FI")?.text || ""
    : "",
  contentEn: announcement
    ? announcement.messages.find((m) => m.languageCode === "EN")?.text || ""
    : "",
  severity: announcement?.severity || Severity.Info,
  id: announcement?.id || "",
  version: announcement?.version || 0,
});

export const isValidAnnouncementDateRange = (
  value: [Date | null, Date | null],
) => {
  const [start, end] = value ?? [];
  if (!start || !end || !isValid(start) || !isValid(end)) {
    return false;
  }
  return isAfter(end, start);
};

export const constructPayload = (data: FormValues): AnnouncementPayload => {
  return {
    startAt: data.dateRange[0].toISOString(),
    endAt: data.dateRange[1].toISOString(),
    targetGroups: data.appTypes,
    messages: [
      { languageCode: "FI", text: data.contentFi },
      { languageCode: "EN", text: data.contentEn },
    ],
    severity: data.severity,
  };
};

export const getBannerColor = (severity: SeverityType) => {
  switch (severity) {
    case Severity.Critical:
      return "#F5B4BA";
    case Severity.Warning:
      return "#F5C891";
    case Severity.Info:
    default:
      return "#F4E4AF";
  }
};
